import { layerLabel } from '@/utils/layerLabel';
import { useLayerStore } from '@/store/layer.store';
import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useEffect } from 'react';
import { Palette, Paintbrush, RotateCcw } from 'lucide-react';
import { DraggablePanel } from '../common/DraggablePanel';
import { useMapStore } from '../../store/map.store';
import TileWMS from 'ol/source/TileWMS';
import ImageWMS from 'ol/source/ImageWMS';
import { useToast } from '../common/Toast';

interface LayerStyleOption {
  name: string;
  label: string;
  /** What GeoServer calls it (workspace:layer), which the SLD must name. */
  wmsName: string;
}

interface EditStylePanelProps {
  onClose: () => void;
}

export const EditStylePanel: React.FC<EditStylePanelProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const mapInstance = useMapStore((s) => s.mapInstance);
  const { addToast } = useToast();

  const [layers, setLayers] = useState<LayerStyleOption[]>([]);
  const [selectedLayer, setSelectedLayer] = useState('');
  const [fillColor, setFillColor] = useState('#307faf');
  const [fillOpacity, setFillOpacity] = useState(0.4);
  const [strokeColor, setStrokeColor] = useState('#307faf');
  const [strokeWidth, setStrokeWidth] = useState(1.5);
  const [pointRadius, setPointRadius] = useState(6);
  const [pointColor, setPointColor] = useState('#e53e3e');

  useEffect(() => {
    if (!mapInstance) return;
    const layerList: LayerStyleOption[] = [];
    mapInstance.getLayers().forEach((layer: any) => {
      const src = layer.getSource?.();
      if (src && (src instanceof TileWMS || src instanceof ImageWMS)) {
        const name = layer.get('name') || '';
        const wmsName = (src.getParams?.() || {}).LAYERS || layer.get('layerName') || name;
        if (name && name !== 'wms-layer') {
          const db = useLayerStore.getState().allLayers.find((l) => l.layerName === layer.get('layerName'));
          layerList.push({ name, label: db ? layerLabel(db) : name, wmsName });
        }
      }
    });
    setLayers(layerList);
    if (layerList.length > 0 && !selectedLayer) {
      setSelectedLayer(layerList[0].name);
    }
  }, [mapInstance]);

  const buildSld = useCallback(() => {
    if (!selectedLayer) return '';
    const wmsName = layers.find((l) => l.name === selectedLayer)?.wmsName || selectedLayer;
    return `<?xml version="1.0" encoding="UTF-8"?>
<StyledLayerDescriptor version="1.0.0"
  xsi:schemaLocation="http://www.opengis.net/sld StyledLayerDescriptor.xsd"
  xmlns="http://www.opengis.net/sld"
  xmlns:ogc="http://www.opengis.net/ogc"
  xmlns:xlink="http://www.w3.org/1999/xlink"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <NamedLayer>
    <Name>${wmsName}</Name>
    <UserStyle>
      <Title>Custom Style</Title>
      <FeatureTypeStyle>
        <Rule>
          <PolygonSymbolizer>
            <Fill>
              <CssParameter name="fill">${fillColor}</CssParameter>
              <CssParameter name="fill-opacity">${fillOpacity}</CssParameter>
            </Fill>
            <Stroke>
              <CssParameter name="stroke">${strokeColor}</CssParameter>
              <CssParameter name="stroke-width">${strokeWidth}</CssParameter>
            </Stroke>
          </PolygonSymbolizer>
          <PointSymbolizer>
            <Graphic>
              <Mark>
                <WellKnownName>circle</WellKnownName>
                <Fill>
                  <CssParameter name="fill">${pointColor}</CssParameter>
                </Fill>
              </Mark>
              <Size>${pointRadius * 2}</Size>
            </Graphic>
          </PointSymbolizer>
          <LineSymbolizer>
            <Stroke>
              <CssParameter name="stroke">${strokeColor}</CssParameter>
              <CssParameter name="stroke-width">${strokeWidth}</CssParameter>
            </Stroke>
          </LineSymbolizer>
        </Rule>
      </FeatureTypeStyle>
    </UserStyle>
  </NamedLayer>
</StyledLayerDescriptor>`;
  }, [selectedLayer, layers, fillColor, fillOpacity, strokeColor, strokeWidth, pointColor, pointRadius]);

  const applyStyle = useCallback(() => {
    if (!mapInstance || !selectedLayer) return;
    const sld = buildSld();
    mapInstance.getLayers().forEach((layer: any) => {
      if (layer.get('name') === selectedLayer) {
        const src = layer.getSource?.();
        if (src && (src instanceof TileWMS || src instanceof ImageWMS)) {
          // The map's proxy may refuse a style sent with the request: then the layer's own style comes back.
          const failed = () => {
            src.updateParams({ SLD_BODY: undefined });
            addToast(t('style.cannot_restyle'), 'warning');
          };
          if (src instanceof ImageWMS) src.once('imageloaderror', failed);
          else src.once('tileloaderror', failed);
          src.updateParams({ SLD_BODY: sld });
        }
      }
    });
  }, [mapInstance, selectedLayer, buildSld, addToast]);

  const resetStyle = useCallback(() => {
    if (!mapInstance || !selectedLayer) return;
    mapInstance.getLayers().forEach((layer: any) => {
      if (layer.get('name') === selectedLayer) {
        const src = layer.getSource?.();
        if (src && (src instanceof TileWMS || src instanceof ImageWMS)) {
          src.updateParams({ SLD_BODY: undefined, STYLES: '' });
        }
      }
    });
  }, [mapInstance, selectedLayer]);

  return (
    <DraggablePanel
      title={t('tools.edit_style')}
      onClose={onClose}
      icon={<Palette size={14} />}
      width={300}
    >
      <div className="p-3 space-y-3">
        {/* Layer selector */}
        <div>
          <span className="text-[10px] text-gray-400 font-medium block mb-1">{t('style.layer')}</span>
          <select
            value={selectedLayer}
            onChange={(e) => setSelectedLayer(e.target.value)}
            className="w-full px-2 py-1.5 text-xs text-gray-200 bg-gray-700 border border-gray-600 rounded focus:border-blue-400 focus:outline-none"
          >
            {layers.map((l) => (
              <option key={l.name} value={l.name}>{l.label}</option>
            ))}
          </select>
        </div>

        {/* Fill */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="block text-[10px] text-gray-400 mb-1">{t('style.fill_color')}</span>
            <input type="color" value={fillColor} onChange={(e) => setFillColor(e.target.value)}
              className="w-full h-7 rounded border border-gray-600 cursor-pointer bg-gray-700" />
          </div>
          <div>
            <span className="block text-[10px] text-gray-400 mb-1">{t('style.fill_opacity')}</span>
            <input type="range" min="0" max="1" step="0.05" value={fillOpacity}
              onChange={(e) => setFillOpacity(parseFloat(e.target.value))}
              className="w-full accent-blue-500" />
            <span className="text-[9px] text-gray-500">{(fillOpacity * 100).toFixed(0)}%</span>
          </div>
        </div>

        {/* Stroke */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="block text-[10px] text-gray-400 mb-1">{t('style.stroke_color')}</span>
            <input type="color" value={strokeColor} onChange={(e) => setStrokeColor(e.target.value)}
              className="w-full h-7 rounded border border-gray-600 cursor-pointer bg-gray-700" />
          </div>
          <div>
            <span className="block text-[10px] text-gray-400 mb-1">{t('style.stroke_width')}</span>
            <input type="number" min="0.5" max="10" step="0.5" value={strokeWidth}
              onChange={(e) => setStrokeWidth(parseFloat(e.target.value))}
              className="w-full px-2 py-1 text-[11px] text-gray-200 bg-gray-700 border border-gray-600 rounded text-center focus:border-blue-400 focus:outline-none" />
          </div>
        </div>

        {/* Point */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="block text-[10px] text-gray-400 mb-1">{t('style.point_color')}</span>
            <input type="color" value={pointColor} onChange={(e) => setPointColor(e.target.value)}
              className="w-full h-7 rounded border border-gray-600 cursor-pointer bg-gray-700" />
          </div>
          <div>
            <span className="block text-[10px] text-gray-400 mb-1">{t('style.point_size')}</span>
            <input type="number" min="2" max="20" step="1" value={pointRadius}
              onChange={(e) => setPointRadius(parseInt(e.target.value))}
              className="w-full px-2 py-1 text-[11px] text-gray-200 bg-gray-700 border border-gray-600 rounded text-center focus:border-blue-400 focus:outline-none" />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            onClick={applyStyle}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-500 transition-colors"
          >
            <Paintbrush size={12} /> {t('buttons.apply')}
          </button>
          <button
            onClick={resetStyle}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-medium border border-gray-600/40 text-gray-300 hover:bg-gray-700/60 transition-colors"
          >
            <RotateCcw size={12} /> {t('buttons.reset')}
          </button>
        </div>
      </div>
    </DraggablePanel>
  );
};
